import { getGuestCartId } from '../helpers/cart-storage';
import {
  applyHeaderContext,
  fetchHeaderContext,
  HEADER_CONTEXT_READY_EVENT,
  readPageContext
} from './shared-header-context';

export default function header(el: HTMLElement): () => void {
  if (el.dataset.headerReady) return () => {};
  el.dataset.headerReady = 'true';

  void hydrateHeaderIfNeeded(el);

  const desktopNav = el.querySelector<HTMLElement>('[data-desktop-nav]');
  const primaryNav = el.querySelector<HTMLElement>('[data-primary-nav]');
  const hamburger = el.querySelector<HTMLButtonElement>('[data-hamburger]');
  const hamburgerIcon = el.querySelector<HTMLElement>('[data-hamburger-icon]');
  const mobileCloseTrigger = el.querySelector<HTMLButtonElement>('[data-mobile-close-trigger]');
  const mobileNav = el.querySelector<HTMLElement>('[data-mobile-nav]');
  const mobileMenu = el.querySelector<HTMLElement>('[data-mobile-menu]');

  const controller = new AbortController();
  const { signal } = controller;

  let activeMegaMenuId: string | null = null;
  let activeDropdownEl: HTMLElement | null = null;
  let activeMobileTrigger: HTMLButtonElement | null = null;
  let suppressKeyboardClickMegaMenuId: string | null = null;
  let pendingKeyboardOpenMegaMenuId: string | null = null;

  function syncLoginLinksWithCartId(cartId: string | null) {
    el.querySelectorAll<HTMLAnchorElement>('[data-login-link]').forEach(link => {
      try {
        const url = new URL(link.href, window.location.origin);

        if (cartId) {
          url.searchParams.set('anonymousCartId', cartId);
        } else {
          url.searchParams.delete('anonymousCartId');
        }

        link.href = url.toString();
      } catch {
        /* malformed href */
      }
    });
  }

  function closeMobileUtilityPanels() {
    mobileMenu?.querySelectorAll<HTMLElement>('[data-mobile-utility-panel]').forEach(panel => {
      panel.removeAttribute('data-open');
      panel.setAttribute('aria-hidden', 'true');
    });
    mobileMenu
      ?.querySelectorAll<HTMLButtonElement>('[data-mobile-utility-trigger]')
      .forEach(btn => {
        btn.removeAttribute('data-open');
        btn.setAttribute('aria-expanded', 'false');
      });
  }

  // ── Mega menu (desktop) ────────────────────────────────────────────────────

  function openMegaMenu(id: string) {
    if (activeMegaMenuId === id) return;
    if (activeMegaMenuId) closeMegaMenu(activeMegaMenuId);
    if (activeDropdownEl) closeDropdown(activeDropdownEl);

    const trigger = desktopNav?.querySelector<HTMLButtonElement>(`[data-nav-trigger="${id}"]`);
    const panel = desktopNav?.querySelector<HTMLElement>(`[data-mega-panel="${id}"]`);
    const chevron = trigger?.querySelector<HTMLElement>('[data-nav-chevron]');

    if (!trigger || !panel) return;

    activeMegaMenuId = id;
    trigger.setAttribute('aria-expanded', 'true');
    trigger.dataset.active = '';
    chevron?.setAttribute('data-active', '');
    panel.removeAttribute('aria-hidden');
    panel.setAttribute('data-open', '');
  }

  function closeMegaMenu(id: string) {
    const trigger = desktopNav?.querySelector<HTMLButtonElement>(`[data-nav-trigger="${id}"]`);
    const panel = desktopNav?.querySelector<HTMLElement>(`[data-mega-panel="${id}"]`);
    const chevron = trigger?.querySelector<HTMLElement>('[data-nav-chevron]');

    if (!trigger || !panel) return;

    trigger.setAttribute('aria-expanded', 'false');
    delete trigger.dataset.active;
    chevron?.removeAttribute('data-active');
    panel.removeAttribute('data-open');
    panel.setAttribute('aria-hidden', 'true');

    if (activeMegaMenuId === id) activeMegaMenuId = null;
  }

  function closeAllMegaMenus() {
    if (activeMegaMenuId) closeMegaMenu(activeMegaMenuId);
  }

  function getMegaMenuFocusableElements(id: string) {
    const panel = desktopNav?.querySelector<HTMLElement>(`[data-mega-panel="${id}"]`);
    if (!panel) return [] as HTMLElement[];

    return Array.from(
      panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(node => !node.hasAttribute('hidden') && node.getAttribute('aria-hidden') !== 'true');
  }

  function focusFirstMegaMenuElement(id: string) {
    requestAnimationFrame(() => {
      window.setTimeout(() => {
        const firstFocusable = getMegaMenuFocusableElements(id)[0];
        firstFocusable?.focus();
      }, 0);
    });
  }

  function getNextHeaderFocusable(afterTrigger: HTMLElement): HTMLElement | null {
    const allHeaderFocusable = Array.from(
      el.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])'
      )
    ).filter(
      node =>
        !node.closest('[hidden]') &&
        node.getAttribute('aria-hidden') !== 'true' &&
        !node.closest('[data-mega-panel]')
    );
    const triggerIndex = allHeaderFocusable.indexOf(afterTrigger);
    return allHeaderFocusable[triggerIndex + 1] ?? null;
  }

  desktopNav?.querySelectorAll<HTMLButtonElement>('[data-nav-trigger]').forEach(trigger => {
    trigger.addEventListener('click', (event: MouseEvent) => {
      const id = trigger.dataset.navTrigger!;
      if (event.detail === 0 && suppressKeyboardClickMegaMenuId === id) {
        suppressKeyboardClickMegaMenuId = null;
        return;
      }

      if (activeMegaMenuId === id) {
        closeMegaMenu(id);
      } else {
        openMegaMenu(id);
      }
    });

    trigger.addEventListener('keydown', (event: KeyboardEvent) => {
      const id = trigger.dataset.navTrigger!;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        pendingKeyboardOpenMegaMenuId = id;
      }

      if (event.key === 'Tab' && !event.shiftKey && activeMegaMenuId === id) {
        event.preventDefault();
        focusFirstMegaMenuElement(id);
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        openMegaMenu(id);
        focusFirstMegaMenuElement(id);
      }
    });

    trigger.addEventListener('keyup', (event: KeyboardEvent) => {
      const id = trigger.dataset.navTrigger!;
      if ((event.key !== 'Enter' && event.key !== ' ') || pendingKeyboardOpenMegaMenuId !== id) {
        return;
      }

      event.preventDefault();
      pendingKeyboardOpenMegaMenuId = null;
      suppressKeyboardClickMegaMenuId = id;

      if (activeMegaMenuId === id) {
        closeMegaMenu(id);
        return;
      }

      openMegaMenu(id);
    });
  });

  // ── Small dropdowns (account, language, country) ───────────────────────────

  function openDropdown(triggerEl: HTMLElement, panelEl: HTMLElement) {
    if (activeDropdownEl && activeDropdownEl !== panelEl) {
      closeDropdown(activeDropdownEl);
    }
    activeDropdownEl = panelEl;
    triggerEl.setAttribute('aria-expanded', 'true');
    panelEl.removeAttribute('aria-hidden');
    panelEl.setAttribute('data-open', '');
    panelEl.focus();
  }

  function closeDropdown(panelEl: HTMLElement) {
    const trigger = el.querySelector<HTMLElement>(`[aria-controls="${panelEl.id}"]`);
    trigger?.setAttribute('aria-expanded', 'false');
    panelEl.removeAttribute('data-open');
    panelEl.setAttribute('aria-hidden', 'true');
    if (activeDropdownEl === panelEl) activeDropdownEl = null;
  }

  function bindDropdownDismiss(trigger: HTMLElement, panel: HTMLElement) {
    trigger.parentElement?.addEventListener(
      'focusout',
      (e: FocusEvent) => {
        if (!trigger.parentElement?.contains(e.relatedTarget as Node | null)) {
          closeDropdown(panel);
        }
      },
      { signal }
    );
  }

  function bindMenuKeyboard(trigger: HTMLElement, menu: HTMLElement) {
    let menuIndex = -1;

    function getMenuItems() {
      return Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]'));
    }

    function focusMenuItem(index: number) {
      const items = getMenuItems();
      if (!items.length) return;
      menuIndex = ((index % items.length) + items.length) % items.length;
      items[menuIndex]?.focus();
    }

    function openMenu(focusIndex = 0) {
      menuIndex = -1;
      openDropdown(trigger, menu);
      requestAnimationFrame(() => focusMenuItem(focusIndex));
    }

    function closeMenu() {
      menuIndex = -1;
      closeDropdown(menu);
    }

    trigger.addEventListener(
      'click',
      () => {
        if (menu.hasAttribute('data-open')) {
          closeMenu();
        } else {
          openMenu(0);
        }
      },
      { signal }
    );

    trigger.addEventListener(
      'keydown',
      (e: KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          openMenu(0);
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          openMenu(getMenuItems().length - 1);
        }
      },
      { signal }
    );

    menu.addEventListener(
      'keydown',
      (e: KeyboardEvent) => {
        const items = getMenuItems();

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          focusMenuItem(menuIndex + 1);
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          focusMenuItem(menuIndex - 1);
        }
        if (e.key === 'Home') {
          e.preventDefault();
          focusMenuItem(0);
        }
        if (e.key === 'End') {
          e.preventDefault();
          focusMenuItem(items.length - 1);
        }
        if (e.key === 'Tab') {
          closeMenu();
        }
      },
      { signal }
    );

    bindDropdownDismiss(trigger, menu);
  }

  el.querySelectorAll<HTMLElement>('[aria-haspopup="menu"]').forEach(trigger => {
    const menuId = trigger.getAttribute('aria-controls');
    const menu = menuId ? el.querySelector<HTMLElement>(`#${menuId}[role="menu"]`) : null;
    if (!menu) return;
    bindMenuKeyboard(trigger, menu);
  });

  desktopNav?.addEventListener('focusout', (e: FocusEvent) => {
    const next = e.relatedTarget as Node | null;
    if (activeMegaMenuId && !desktopNav.contains(next)) {
      closeAllMegaMenus();
    }
  });

  desktopNav?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key !== 'Tab' || e.shiftKey || !activeMegaMenuId) return;
    const focusable = getMegaMenuFocusableElements(activeMegaMenuId);
    const last = focusable[focusable.length - 1];
    if (!last || document.activeElement !== last) return;

    const trigger = desktopNav.querySelector<HTMLElement>(
      `[data-nav-trigger="${activeMegaMenuId}"]`
    );
    const nextControl = trigger ? getNextHeaderFocusable(trigger) : null;
    if (!nextControl) return;

    e.preventDefault();
    closeMegaMenu(activeMegaMenuId);
    nextControl.focus();
  });

  const languageTrigger = el.querySelector<HTMLButtonElement>('[data-language-trigger]');
  const languageMenu = el.querySelector<HTMLElement>('[data-language-menu]');
  const countryTrigger = el.querySelector<HTMLButtonElement>('[data-country-trigger]');
  const countryMenu = el.querySelector<HTMLElement>('[data-country-menu]');
  const countrySearch = el.querySelector<HTMLInputElement>('[data-country-search]');

  function getLanguageOptions() {
    return Array.from(
      languageMenu?.querySelectorAll<HTMLAnchorElement>('[data-language-option]') ?? []
    );
  }

  function getCurrentLanguageIndex() {
    const options = getLanguageOptions();
    const currentIndex = options.findIndex(
      option => option.getAttribute('aria-current') === 'true'
    );
    return currentIndex >= 0 ? currentIndex : 0;
  }

  function focusLanguageOption(index: number) {
    const options = getLanguageOptions();
    if (!options.length) return;

    const nextIndex = Math.max(0, Math.min(index, options.length - 1));

    options[nextIndex]?.focus();
  }

  function openLanguageDropdown(focusIndex: number = getCurrentLanguageIndex()) {
    if (!languageTrigger || !languageMenu) return;

    openDropdown(languageTrigger, languageMenu);
    requestAnimationFrame(() => focusLanguageOption(focusIndex));
  }

  if (languageTrigger && languageMenu) {
    languageTrigger.addEventListener('click', () => {
      if (languageMenu.hasAttribute('data-open')) {
        closeDropdown(languageMenu);
      } else {
        openLanguageDropdown();
      }
    });

    languageTrigger.addEventListener('keydown', (event: KeyboardEvent) => {
      const options = getLanguageOptions();
      if (!options.length) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        openLanguageDropdown(0);
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        openLanguageDropdown(options.length - 1);
      }
    });

    languageMenu.addEventListener('keydown', (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const activeOption = target?.closest<HTMLAnchorElement>('[data-language-option]');
      if (!activeOption) return;

      const options = getLanguageOptions();
      const currentIndex = options.indexOf(activeOption);
      if (currentIndex < 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        focusLanguageOption(currentIndex + 1);
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        focusLanguageOption(currentIndex - 1);
      }

      if (event.key === 'Home') {
        event.preventDefault();
        focusLanguageOption(0);
      }

      if (event.key === 'End') {
        event.preventDefault();
        focusLanguageOption(options.length - 1);
      }

      if (event.key === ' ') {
        event.preventDefault();
        activeOption.click();
      }
    });

    bindDropdownDismiss(languageTrigger, languageMenu);
  }

  function getCountryOptions() {
    return Array.from(
      countryMenu?.querySelectorAll<HTMLAnchorElement>('[data-country-option]') ?? []
    );
  }

  function getVisibleCountryOptions() {
    return getCountryOptions().filter(option => {
      const item = option.closest<HTMLElement>('[data-country-option-item]');
      return item?.hasAttribute('hidden') !== true;
    });
  }

  function focusCountryOption(index: number) {
    const options = getVisibleCountryOptions();
    if (!options.length) return;

    const nextIndex = Math.max(0, Math.min(index, options.length - 1));

    getCountryOptions().forEach(option => {
      option.tabIndex = -1;
    });

    options.forEach((option, optionIndex) => {
      option.tabIndex = optionIndex === nextIndex ? 0 : -1;
    });

    options[nextIndex]?.focus();
  }

  function filterCountryOptions(query: string) {
    const normalizedQuery = query.trim().toLowerCase();
    getCountryOptions().forEach(option => {
      const name = option.dataset.countryName?.toLowerCase() ?? '';
      const item = option.closest<HTMLElement>('[data-country-option-item]');
      if (!item) return;

      const isVisible = normalizedQuery.length === 0 || name.includes(normalizedQuery);
      item.toggleAttribute('hidden', !isVisible);
    });
  }

  function resetCountryFilter() {
    if (countrySearch) {
      countrySearch.value = '';
    }
    filterCountryOptions('');
  }

  function openCountryDropdown(focusIndex?: number) {
    if (!countryTrigger || !countryMenu) return;

    openDropdown(countryTrigger, countryMenu);
    requestAnimationFrame(() => {
      if (countrySearch && focusIndex === undefined) {
        countrySearch.focus();
      }
      if (focusIndex !== undefined) {
        focusCountryOption(focusIndex);
      }
    });
  }

  if (countryTrigger && countryMenu) {
    countryTrigger.addEventListener('click', () => {
      if (countryMenu.hasAttribute('data-open')) {
        closeDropdown(countryMenu);
      } else {
        resetCountryFilter();
        openCountryDropdown();
      }
    });

    countryTrigger.addEventListener('keydown', (event: KeyboardEvent) => {
      const options = getVisibleCountryOptions();
      if (!options.length) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        resetCountryFilter();
        openCountryDropdown(0);
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        resetCountryFilter();
        openCountryDropdown(options.length - 1);
      }
    });

    countrySearch?.addEventListener('input', () => {
      filterCountryOptions(countrySearch.value);
    });

    countrySearch?.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        focusCountryOption(0);
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        closeDropdown(countryMenu);
        countryTrigger.focus();
      }
    });

    countryMenu.addEventListener('keydown', (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const activeOption = target?.closest<HTMLAnchorElement>('[data-country-option]');
      if (!activeOption) return;

      const options = getVisibleCountryOptions();
      const currentIndex = options.indexOf(activeOption);
      if (currentIndex < 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        focusCountryOption(currentIndex + 1);
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        focusCountryOption(currentIndex - 1);
      }

      if (event.key === 'Home') {
        event.preventDefault();
        focusCountryOption(0);
      }

      if (event.key === 'End') {
        event.preventDefault();
        focusCountryOption(options.length - 1);
      }

      if (event.key === ' ') {
        event.preventDefault();
        activeOption.click();
      }

      if (event.key === 'Tab') {
        closeDropdown(countryMenu);
      }
    });

    bindDropdownDismiss(countryTrigger, countryMenu);
  }

  // ── Mobile menu ────────────────────────────────────────────────────────────

  function openMobileMenu() {
    if (!hamburger || !mobileMenu) return;
    if (mobileNav) {
      const mobileMenuBarHeight = mobileNav.offsetHeight;
      mobileMenu.style.setProperty('--mobile-menu-bar-height', `${mobileMenuBarHeight}px`);
      el.style.setProperty('--mobile-menu-bar-height', `${mobileMenuBarHeight}px`);
    }
    hamburger.setAttribute('aria-expanded', 'true');
    hamburger.setAttribute('aria-label', 'Close menu');
    hamburger.classList.add('hidden');
    hamburger.classList.remove('flex');
    hamburgerIcon?.classList.add('hidden');
    mobileCloseTrigger?.classList.remove('hidden');
    mobileCloseTrigger?.classList.add('flex');
    mobileNav?.setAttribute('data-open', '');
    el.setAttribute('data-mobile-open', '');
    mobileMenu.removeAttribute('aria-hidden');
    mobileMenu.setAttribute('data-open', '');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => mobileCloseTrigger?.focus());
  }

  function closeMobileMenu() {
    if (!hamburger || !mobileMenu) return;
    hamburger.setAttribute('aria-expanded', 'false');
    hamburger.setAttribute('aria-label', 'Open menu');
    hamburger.classList.remove('hidden');
    hamburger.classList.add('flex');
    hamburgerIcon?.classList.remove('hidden');
    mobileCloseTrigger?.classList.add('hidden');
    mobileCloseTrigger?.classList.remove('flex');
    mobileNav?.removeAttribute('data-open');
    el.removeAttribute('data-mobile-open');
    mobileMenu.removeAttribute('data-open');
    mobileMenu.setAttribute('aria-hidden', 'true');
    mobileMenu.style.removeProperty('--mobile-menu-bar-height');
    el.style.removeProperty('--mobile-menu-bar-height');
    document.body.style.overflow = '';
    closeMobileUtilityPanels();
    closeMobileSecondLevels();
  }

  hamburger?.addEventListener('click', () => {
    if (mobileMenu?.hasAttribute('data-open')) {
      closeMobileMenu();
    } else {
      openMobileMenu();
    }
  });

  window.addEventListener(
    'resize',
    () => {
      if (mobileMenu?.hasAttribute('data-open') && mobileNav) {
        const mobileMenuBarHeight = mobileNav.offsetHeight;
        mobileMenu.style.setProperty('--mobile-menu-bar-height', `${mobileMenuBarHeight}px`);
        el.style.setProperty('--mobile-menu-bar-height', `${mobileMenuBarHeight}px`);
      }
    },
    { signal }
  );

  mobileCloseTrigger?.addEventListener('click', () => {
    closeMobileMenu();
    hamburger?.focus();
  });

  mobileMenu
    ?.querySelectorAll<HTMLButtonElement>('[data-mobile-utility-trigger]')
    .forEach(trigger => {
      trigger.addEventListener('click', () => {
        const id = trigger.dataset.mobileUtilityTrigger!;
        const panel = mobileMenu.querySelector<HTMLElement>(`[data-mobile-utility-panel="${id}"]`);
        if (!panel) return;

        const isOpen = panel.hasAttribute('data-open');
        closeMobileUtilityPanels();
        if (isOpen) return;

        trigger.setAttribute('aria-expanded', 'true');
        trigger.setAttribute('data-open', '');
        panel.setAttribute('data-open', '');
        panel.setAttribute('aria-hidden', 'false');
      });
    });

  // ── Mobile second-level navigation ────────────────────────────────────────

  function closeMobileSecondLevels() {
    mobileMenu?.querySelectorAll<HTMLElement>('[data-mobile-second-level]').forEach(panel => {
      panel.removeAttribute('data-open');
      panel.setAttribute('aria-hidden', 'true');
    });
    mobileMenu?.querySelectorAll<HTMLButtonElement>('[data-mobile-nav-trigger]').forEach(btn => {
      btn.setAttribute('aria-expanded', 'false');
    });
    const firstLevel = mobileMenu?.querySelector<HTMLElement>('[data-mobile-first-level]');
    if (firstLevel) firstLevel.style.display = '';
    activeMobileTrigger?.focus();
    activeMobileTrigger = null;
  }

  mobileMenu?.querySelectorAll<HTMLButtonElement>('[data-mobile-nav-trigger]').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const id = trigger.dataset.mobileNavTrigger!;
      const panel = mobileMenu.querySelector<HTMLElement>(`[data-mobile-second-level="${id}"]`);
      const firstLevel = mobileMenu.querySelector<HTMLElement>('[data-mobile-first-level]');
      if (!panel) return;

      closeMobileUtilityPanels();
      activeMobileTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
      panel.removeAttribute('aria-hidden');
      panel.setAttribute('data-open', '');
      if (firstLevel) firstLevel.style.display = 'none';
      panel.querySelector<HTMLElement>('[data-mobile-back]')?.focus();
    });
  });

  mobileMenu?.querySelectorAll<HTMLButtonElement>('[data-mobile-back]').forEach(backBtn => {
    backBtn.addEventListener('click', () => {
      closeMobileSecondLevels();
      const firstLevel = mobileMenu?.querySelector<HTMLElement>('[data-mobile-first-level]');
      if (firstLevel) firstLevel.style.display = '';
    });
  });

  // ── Search overlay triggers (desktop + mobile) ──────────────────────────────

  let lastSearchTrigger: HTMLButtonElement | null = null;

  el.querySelectorAll<HTMLButtonElement>('[data-search-overlay-trigger]').forEach(trigger => {
    trigger.addEventListener(
      'click',
      () => {
        lastSearchTrigger = trigger;
        trigger.setAttribute('aria-expanded', 'true');
        document
          .querySelector<HTMLElement>('[data-module="search-overlay"]')
          ?.dispatchEvent(new CustomEvent('search-overlay:open', { bubbles: true }));
      },
      { signal }
    );
  });

  // The overlay owns focus restoration; sync aria-expanded on the trigger.
  document.addEventListener(
    'search-overlay:closed',
    () => {
      lastSearchTrigger?.setAttribute('aria-expanded', 'false');
      lastSearchTrigger = null;
    },
    { signal }
  );

  // ── Global close: click outside / Escape ─────────────────────────────────

  document.addEventListener(
    'click',
    (e: MouseEvent) => {
      const target = e.target as Node;

      if (!el.contains(target)) {
        closeAllMegaMenus();
        if (activeDropdownEl) closeDropdown(activeDropdownEl);
      }
    },
    { signal }
  );

  document.addEventListener(
    'keydown',
    (e: KeyboardEvent) => {
      if (e.key === 'Tab' && mobileMenu?.hasAttribute('data-open')) {
        const focusable = Array.from(
          mobileMenu.querySelectorAll<HTMLElement>(
            'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])'
          )
        ).filter(
          node => !node.closest('[hidden]') && node.closest('[aria-hidden="true"]') === null
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || !last) return;
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
        return;
      }

      if (e.key !== 'Escape') return;

      if (activeMegaMenuId) {
        const id = activeMegaMenuId;
        closeAllMegaMenus();
        desktopNav?.querySelector<HTMLElement>(`[data-nav-trigger="${id}"]`)?.focus();
        return;
      }

      if (activeDropdownEl) {
        const panel = activeDropdownEl;
        const triggerId = panel.getAttribute('id');
        const trigger = triggerId
          ? el.querySelector<HTMLElement>(`[aria-controls="${triggerId}"]`)
          : null;
        closeDropdown(panel);
        trigger?.focus();
        return;
      }

      if (mobileMenu?.hasAttribute('data-open')) {
        closeMobileMenu();
        hamburger?.focus();
      }
    },
    { signal }
  );

  document.addEventListener(
    'scroll',
    () => {
      if (activeDropdownEl) closeDropdown(activeDropdownEl);
      if (activeMegaMenuId) closeAllMegaMenus();
    },
    { signal, passive: true }
  );

  // ── Resize: close mobile menu when switching to desktop ───────────────────

  const mediaQuery = window.matchMedia('(min-width: 1024px)');
  mediaQuery.addEventListener(
    'change',
    (e: MediaQueryListEvent) => {
      if (e.matches) closeMobileMenu();
      updateDesktopMode();
    },
    { signal }
  );

  let forceMobileThreshold: number | null = null;
  const FORCE_MOBILE_HYSTERESIS = 32;

  function updateDesktopMode() {
    if (!desktopNav || !primaryNav || !mobileNav) return;

    if (!mediaQuery.matches) {
      delete el.dataset.forceMobile;
      forceMobileThreshold = null;
      return;
    }

    if ('forceMobile' in el.dataset) {
      if (
        forceMobileThreshold !== null &&
        window.innerWidth > forceMobileThreshold + FORCE_MOBILE_HYSTERESIS
      ) {
        if (mobileMenu?.hasAttribute('data-open')) closeMobileMenu();
        delete el.dataset.forceMobile;
        forceMobileThreshold = null;
      }
      return;
    }

    const shouldForceMobile = primaryNav.scrollWidth > primaryNav.clientWidth + 1;
    if (shouldForceMobile) {
      forceMobileThreshold = window.innerWidth;
      el.dataset.forceMobile = '';
      closeAllMegaMenus();
      if (activeDropdownEl) closeDropdown(activeDropdownEl);
    }
  }

  let rafPending = false;
  const resizeObserver = new ResizeObserver(() => {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => {
      rafPending = false;
      updateDesktopMode();
    });
  });

  if (primaryNav) resizeObserver.observe(primaryNav);
  if (mobileNav) resizeObserver.observe(mobileNav);

  requestAnimationFrame(updateDesktopMode);

  try {
    // Only guests carry an anonymous cart; never decorate links once authenticated.
    const isAuthenticated = document.querySelector('[data-authenticated-cart-id]') !== null;
    const cartId = isAuthenticated ? null : getGuestCartId();
    syncLoginLinksWithCartId(cartId);
  } catch {
    /* localStorage unavailable */
  }

  document.addEventListener(
    'bentley:cart-state-updated',
    (e: Event) => {
      const { cartId, authenticated } = (
        e as CustomEvent<{ cartId: string | null; authenticated?: boolean }>
      ).detail;
      syncLoginLinksWithCartId(authenticated ? null : (cartId ?? null));
    },
    { signal }
  );

  const sentinel = document.createElement('div');
  Object.assign(sentinel.style, {
    position: 'absolute',
    top: '0',
    height: '1px',
    width: '1px',
    pointerEvents: 'none',
    visibility: 'hidden'
  });
  document.body.prepend(sentinel);

  const scrollObserver = new IntersectionObserver(entries => {
    const entry = entries[0];
    if (!entry) return;
    el.toggleAttribute('data-scrolled', !entry.isIntersecting);
  });
  scrollObserver.observe(sentinel);

  return (): void => {
    controller.abort();
    resizeObserver.disconnect();
    scrollObserver.disconnect();
    sentinel.remove();
  };
}

async function hydrateHeaderIfNeeded(el: HTMLElement): Promise<void> {
  const pageContext = readPageContext();
  const isSkeleton = el.hasAttribute('data-public-cache-skeleton');

  if (!pageContext?.publicCache || !pageContext.pageContentGuid) {
    // Skeleton mini-cart waits on this event; always release waiters.
    if (isSkeleton) {
      el.dispatchEvent(
        new CustomEvent(HEADER_CONTEXT_READY_EVENT, {
          detail: null,
          bubbles: true
        })
      );
    }
    return;
  }

  try {
    const ctx = await fetchHeaderContext(pageContext.pageContentGuid);
    applyHeaderContext(el, ctx);
    el.dispatchEvent(
      new CustomEvent(HEADER_CONTEXT_READY_EVENT, {
        detail: ctx,
        bubbles: true
      })
    );
  } catch (error) {
    console.error('Failed to hydrate header context', error);
    el.dispatchEvent(
      new CustomEvent(HEADER_CONTEXT_READY_EVENT, {
        detail: null,
        bubbles: true
      })
    );
  }
}
